// src/services/productService.js
import apiClient from './api';

export const productService = {
  getProducts: async (filters = {}) => {
    try {
      console.log('[ProductService] Fetching products from API');

      const response = await apiClient.get('/products/', {
        params: {
          page_size: 1000,
          ...filters
        }
      });
      
      console.log('[ProductService] Products fetched successfully:', response.data);
      
      // Check if response is paginated
      const products = response.data.results || response.data.products || response.data || [];
      
      // Log sample product structure
      if (products.length > 0) {
        console.log('[ProductService] Sample product structure:', products[0]);
        console.log('[ProductService] Total products count:', products.length);
      }
      
      return {
        success: true,
        data: products
      };
    } catch (error) {
      console.error('[ProductService] Error fetching products:', error);
      console.error('[ProductService] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch products'
      };
    }
  },
  
  getProductsLight: async (filters = {}) => {
    try {
      const response = await apiClient.get('/products/', {
        params: { fields: 'light', page_size: 5000, ...filters }
      });
      const products = response.data.results || response.data || [];
      return { success: true, data: products };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getProductsPaginated: async (page = 1, pageSize = 20, filters = {}) => {
    try {
      const response = await apiClient.get('/products/', {
        params: {
          page,
          page_size: pageSize,
          ...filters
        }
      });
      
      return {
        success: true,
        data: response.data.results || [],
        count: response.data.count || 0,
        hasNext: response.data.next || false,
        hasPrevious: response.data.previous || false,
        currentPage: page,
        totalPages: Math.ceil((response.data.count || 0) / pageSize)
      };
    } catch (error) {
      console.error('[ProductService] Error fetching paginated products:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch products'
      };
    }
  },
  
  getProductDetails: async (productId) => {
    try {
      const response = await apiClient.get(`/orders/get_product_details/${productId}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch product details'
      };
    }
  },

  getProductOrderStats: async (productId, customerId = null) => {
    try {
      const params = new URLSearchParams();
      if (customerId) {
        params.append('customer_id', customerId);
      }
      
      const url = `/orders/get_product_order_stats/${productId}/${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      // Log the error for debugging but don't spam console for auth errors
      if (error.response?.status !== 401) {
        console.error(`[ProductService] Error fetching order stats for product ${productId}:`, error);
      }
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        return {
          success: false,
          error: 'Authentication required',
          requiresAuth: true
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch product order statistics'
      };
    }
  },

  getBulkProductOrderStats: async (productIds, customerId = null) => {
    try {
      const params = new URLSearchParams();
      params.append('product_ids', productIds.join(','));
      if (customerId) {
        params.append('customer_id', customerId);
      }
      
      const url = `/orders/bulk_get_product_order_stats/?${params.toString()}`;
      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data.product_stats || {}
      };
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error(`[ProductService] Error fetching bulk order stats:`, error);
      }
      
      if (error.response?.status === 401) {
        return {
          success: false,
          error: 'Authentication required',
          requiresAuth: true
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch bulk product order statistics'
      };
    }
  },

  /**
   * Get products for admin page with pagination and filtering
   */
  getProductsAdmin: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('fields', 'admin');

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await apiClient.get(`/products/?${params.toString()}`);

      if (response.data.results !== undefined) {
        return response.data;
      } else {
        return {
          results: response.data.products || response.data || [],
          count: response.data.products?.length || response.data?.length || 0
        };
      }
    } catch (error) {
      console.error('[ProductService] Error fetching admin products:', error);
      throw error;
    }
  },

  /**
   * Get all product IDs based on filters (for bulk select all)
   */
  getAllProductIds: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Add filters to params (excluding pagination)
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && key !== 'page' && key !== 'page_size') {
          params.append(key, value);
        }
      });

      // Request only IDs with a large page size
      params.append('fields', 'id');
      params.append('page_size', '10000');

      const response = await apiClient.get(`/products/?${params.toString()}`);

      // Extract IDs from response
      const products = response.data.results || response.data.products || response.data || [];
      return products.map(p => p.id);
    } catch (error) {
      console.error('[ProductService] Error fetching all product IDs:', error);
      throw error;
    }
  },

  /**
   * Get all product categories
   * @returns {Promise<Array>} Categories array
   */
  getCategories: async ({ hideOutOfStock = false, hideZeroPrice = false, includeDisabled = false, customerId = null } = {}) => {
    try {
      const params = {};
      if (customerId) params.customer_id = customerId;
      if (hideOutOfStock) params.hide_out_of_stock = 'true';
      if (hideZeroPrice) params.hide_zero_price = 'true';
      if (includeDisabled) params.include_disabled = 'true';
      const response = await apiClient.get('/products/categories/', { params });
      // The backend returns the array directly in response.data
      const data = response.data || [];
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('[ProductService] Error fetching categories:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch categories'
      };
    }
  },

  /**
   * Bulk update products
   * @param {Array} productIds - Array of product IDs to update
   * @param {Object} updates - Object containing fields to update
   * @returns {Promise<Object>} Result of bulk update
   */
  bulkUpdate: async (productIds, updates) => {
    try {
      const response = await apiClient.post('/products/bulk-update/', {
        product_ids: productIds,
        updates: updates
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk updating products:', error);
      throw error;
    }
  },

  /**
   * Create a new product
   * @param {Object} productData - Product data
   * @returns {Promise<Object>} Created product
   */
  createProduct: async (productData) => {
    try {
      const response = await apiClient.post('/products/', productData);
      if (response.status >= 400) {
        const error = new Error(response.data?.detail || 'Failed to create product');
        error.response = response;
        throw error;
      }
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating product:', error);
      throw error;
    }
  },

  /**
   * Update a product
   * @param {number} productId - Product ID
   * @param {Object} productData - Product data to update
   * @returns {Promise<Object>} Updated product
   */
  updateProduct: async (productId, productData) => {
    try {
      const response = await apiClient.patch(`/products/${productId}/`, productData);
      if (response.status >= 400) {
        const error = new Error(response.data?.error || 'Failed to update product');
        error.response = response;
        throw error;
      }
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating product:', error);
      throw error;
    }
  },

  /**
   * Get a single product by ID
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Product data
   */
  getProduct: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching product:', error);
      throw error;
    }
  },

  /**
   * Search products by name or SKU
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Products array
   */
  searchProducts: async (searchTerm, extraParams = {}) => {
    try {
      const response = await apiClient.get('/products/', {
        params: {
          search: searchTerm,
          page_size: 50,
          ...extraParams
        }
      });
      return response.data.results || response.data.products || response.data || [];
    } catch (error) {
      console.error('[ProductService] Error searching products:', error);
      return [];
    }
  },

  /**
   * Get all product groups
   * @returns {Promise<Array>} Groups array
   */
  getGroups: async ({ tree = false } = {}) => {
    try {
      const response = await apiClient.get('/products/groups/', { params: tree ? { tree: 1 } : {} });
      const data = response.data || [];
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('[ProductService] Error fetching groups:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch groups'
      };
    }
  },

  getBrands: async () => {
    try {
      const response = await apiClient.get('/products/brands/');
      const data = response.data || [];
      return { success: true, data: data };
    } catch (error) {
      console.error('[ProductService] Error fetching brands:', error);
      return { success: false, data: [], error: error.response?.data?.error || 'Failed to fetch brands' };
    }
  },

  createBrand: async (name) => {
    try {
      const response = await apiClient.post('/products/brands/create/', { name });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to create brand' };
    }
  },

  updateBrand: async (brandId, data) => {
    try {
      const response = await apiClient.patch(`/products/brands/${brandId}/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating brand:', error);
      throw error;
    }
  },

  deleteBrand: async (brandId) => {
    try {
      const response = await apiClient.delete(`/products/brands/${brandId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error deleting brand:', error);
      throw error;
    }
  },

  /**
   * Get all product units
   * @returns {Promise<Array>} Units array
   */
  getUnits: async () => {
    try {
      const response = await apiClient.get('/products/units/');
      const data = response.data || [];
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('[ProductService] Error fetching units:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch units'
      };
    }
  },

  /**
   * Add extra unit to product
   * @param {number} productId - Product ID
   * @param {Object} extraUnitData - Extra unit data
   * @returns {Promise<Object>} Created extra unit
   */
  addExtraUnit: async (productId, extraUnitData) => {
    try {
      const response = await apiClient.post(`/products/${productId}/extra-units/`, extraUnitData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error adding extra unit:', error);
      throw error;
    }
  },

  /**
   * Delete extra unit from product
   * @param {number} productId - Product ID
   * @param {number} extraUnitId - Extra unit ID
   * @returns {Promise<void>}
   */
  deleteExtraUnit: async (productId, extraUnitId) => {
    try {
      await apiClient.delete(`/products/${productId}/extra-units/${extraUnitId}/delete/`);
    } catch (error) {
      console.error('[ProductService] Error deleting extra unit:', error);
      throw error;
    }
  },

  /**
   * Update extra unit for product
   * @param {number} productId - Product ID
   * @param {number} extraUnitId - Extra unit ID
   * @param {Object} extraUnitData - Extra unit data
   * @returns {Promise<Object>} Updated extra unit
   */
  updateExtraUnit: async (productId, extraUnitId, extraUnitData) => {
    try {
      const response = await apiClient.patch(`/products/${productId}/extra-units/${extraUnitId}/`, extraUnitData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating extra unit:', error);
      throw error;
    }
  },

  getHSCodes: async () => {
    try {
      const response = await apiClient.get('/products/hs-codes/');
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching HS codes:', error);
      throw error;
    }
  },

  createHSCode: async (data) => {
    try {
      const response = await apiClient.post('/products/hs-codes/create/', data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating HS code:', error);
      throw error;
    }
  },

  updateHSCode: async (hsCodeId, data) => {
    try {
      const response = await apiClient.patch(`/products/hs-codes/${hsCodeId}/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating HS code:', error);
      throw error;
    }
  },

  deleteHSCode: async (hsCodeId) => {
    try {
      await apiClient.delete(`/products/hs-codes/${hsCodeId}/delete/`);
    } catch (error) {
      console.error('[ProductService] Error deleting HS code:', error);
      throw error;
    }
  },

  bulkSetHSCode: async (productIds, hsCodeId, countryOfOriginId = null) => {
    try {
      const data = {
        product_ids: productIds,
        hs_code_id: hsCodeId
      };
      if (countryOfOriginId) data.country_of_origin_id = countryOfOriginId;
      const response = await apiClient.post('/products/bulk-set-hs-code/', data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk setting HS code:', error);
      throw error;
    }
  },

  getCustomsData: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/customs-data/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching customs data:', error);
      throw error;
    }
  },

  updateCustomsData: async (productId, data) => {
    try {
      const response = await apiClient.put(`/products/${productId}/customs-data/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating customs data:', error);
      throw error;
    }
  },

  deleteCustomsData: async (productId) => {
    try {
      await apiClient.delete(`/products/${productId}/customs-data/`);
    } catch (error) {
      console.error('[ProductService] Error deleting customs data:', error);
      throw error;
    }
  },

  uploadProductImage: async (productId, formData) => {
    try {
      const response = await apiClient.post(`/products/${productId}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error uploading product image:', error);
      throw error;
    }
  },

  /**
   * Delete product image
   * @param {number} productId - Product ID
   * @param {number} mediaId - Media ID
   * @returns {Promise<void>}
   */
  deleteProductImage: async (productId, mediaId) => {
    try {
      await apiClient.delete(`/products/${productId}/images/${mediaId}/`);
    } catch (error) {
      console.error('[ProductService] Error deleting product image:', error);
      throw error;
    }
  },

  /**
   * Set primary image for product
   * @param {number} productId - Product ID
   * @param {number} mediaId - Media ID
   * @returns {Promise<Object>} Updated media
   */
  setPrimaryImage: async (productId, mediaId) => {
    try {
      const response = await apiClient.patch(`/products/${productId}/images/${mediaId}/set-primary/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error setting primary image:', error);
      throw error;
    }
  },

  getBarcodes: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/barcodes/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching barcodes:', error);
      throw error;
    }
  },

  addBarcode: async (productId, barcodeData) => {
    try {
      const response = await apiClient.post(`/products/${productId}/barcodes/`, barcodeData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error adding barcode:', error);
      throw error;
    }
  },

  updateBarcode: async (productId, barcodeId, barcodeData) => {
    try {
      const response = await apiClient.put(`/products/${productId}/barcodes/${barcodeId}/`, barcodeData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating barcode:', error);
      throw error;
    }
  },

  deleteBarcode: async (productId, barcodeId) => {
    try {
      await apiClient.delete(`/products/${productId}/barcodes/${barcodeId}/`);
    } catch (error) {
      console.error('[ProductService] Error deleting barcode:', error);
      throw error;
    }
  },

  getAllBarcodes: async (params = {}) => {
    try {
      const response = await apiClient.get('/products/barcodes-list/', { params });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching all barcodes:', error);
      throw error;
    }
  },

  bulkUpdateBarcodes: async (action, barcodeIds, extraData = {}) => {
    try {
      const response = await apiClient.post('/products/barcodes-bulk-update/', {
        action,
        barcode_ids: barcodeIds,
        ...extraData
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk updating barcodes:', error);
      throw error;
    }
  },

  bulkImportBarcodes: async (items) => {
    try {
      const response = await apiClient.post('/products/barcodes-bulk-import/', { items });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk importing barcodes:', error);
      throw error;
    }
  },

  getSupplierMappings: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/supplier-mappings/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching supplier mappings:', error);
      throw error;
    }
  },

  addSupplierMapping: async (productId, mappingData) => {
    try {
      const response = await apiClient.post(`/products/${productId}/supplier-mappings/`, mappingData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error adding supplier mapping:', error);
      throw error;
    }
  },

  updateSupplierMapping: async (productId, mappingId, mappingData) => {
    try {
      const response = await apiClient.put(`/products/${productId}/supplier-mappings/${mappingId}/`, mappingData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating supplier mapping:', error);
      throw error;
    }
  },

  deleteSupplierMapping: async (productId, mappingId) => {
    try {
      await apiClient.delete(`/products/${productId}/supplier-mappings/${mappingId}/`);
    } catch (error) {
      console.error('[ProductService] Error deleting supplier mapping:', error);
      throw error;
    }
  },

  lookupByBarcode: async (barcodeValue, supplierId = null, receiptId = null) => {
    try {
      const params = { barcode: barcodeValue };
      if (supplierId) {
        params.supplier_id = supplierId;
      }
      if (receiptId) {
        params.receipt_id = receiptId;
      }
      const response = await apiClient.get(`/products/lookup-barcode/`, { params });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error looking up barcode:', error);
      throw error;
    }
  },

  getBadges: async () => {
    try {
      const response = await apiClient.get('/products/badges/');
      // Handle both paginated and non-paginated response formats
      const badges = response.data.results || response.data || [];
      return {
        success: true,
        data: badges
      };
    } catch (error) {
      console.error('[ProductService] Error fetching badges:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch badges'
      };
    }
  },

  getAllBadges: async () => {
    try {
      const response = await apiClient.get('/products/badges/', {
        params: { page_size: 1000 }
      });
      const badges = response.data.results || response.data || [];
      return {
        success: true,
        data: badges
      };
    } catch (error) {
      console.error('[ProductService] Error fetching all badges:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch badges'
      };
    }
  },

  createBadge: async (badgeData) => {
    try {
      const response = await apiClient.post('/products/badges/', badgeData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating badge:', error);
      throw error;
    }
  },

  updateBadge: async (badgeId, badgeData) => {
    try {
      const response = await apiClient.patch(`/products/badges/${badgeId}/`, badgeData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating badge:', error);
      throw error;
    }
  },

  deleteBadge: async (badgeId) => {
    try {
      await apiClient.delete(`/products/badges/${badgeId}/`);
    } catch (error) {
      console.error('[ProductService] Error deleting badge:', error);
      throw error;
    }
  },

  bulkAddBadges: async (productIds, badgeIds) => {
    try {
      const response = await apiClient.post('/products/badges/bulk-add/', {
        product_ids: productIds,
        badge_ids: badgeIds
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk adding badges:', error);
      throw error;
    }
  },

  bulkRemoveBadges: async (productIds, badgeIds) => {
    try {
      const response = await apiClient.post('/products/badges/bulk-remove/', {
        product_ids: productIds,
        badge_ids: badgeIds
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk removing badges:', error);
      throw error;
    }
  },

  bulkSetBadges: async (productIds, badgeIds) => {
    try {
      const response = await apiClient.post('/products/badges/bulk-set/', {
        product_ids: productIds,
        badge_ids: badgeIds
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk setting badges:', error);
      throw error;
    }
  },

  getKitComponents: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/kit-components/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error fetching kit components:', error);
      throw error;
    }
  },

  addKitComponent: async (productId, componentData) => {
    try {
      const response = await apiClient.post(`/products/${productId}/kit-components/`, componentData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error adding kit component:', error);
      throw error;
    }
  },

  updateKitComponent: async (productId, componentId, componentData) => {
    try {
      const response = await apiClient.put(`/products/${productId}/kit-components/${componentId}/`, componentData);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating kit component:', error);
      throw error;
    }
  },

  deleteKitComponent: async (productId, componentId) => {
    try {
      await apiClient.delete(`/products/${productId}/kit-components/${componentId}/`);
    } catch (error) {
      console.error('[ProductService] Error deleting kit component:', error);
      throw error;
    }
  },

  createUnit: async (data) => {
    try {
      const response = await apiClient.post('/products/units/create/', data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating unit:', error);
      throw error;
    }
  },

  updateUnit: async (unitId, data) => {
    try {
      const response = await apiClient.patch(`/products/units/${unitId}/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating unit:', error);
      throw error;
    }
  },

  deleteUnit: async (unitId) => {
    try {
      const response = await apiClient.delete(`/products/units/${unitId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error deleting unit:', error);
      throw error;
    }
  },

  generateNextSku: async (count = 1, categoryId = null) => {
    try {
      const params = { count };
      if (categoryId) params.category_id = categoryId;
      const response = await apiClient.get('/products/generate-sku/', { params });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error generating SKU:', error);
      throw error;
    }
  },

  createCategory: async (data) => {
    try {
      const response = await apiClient.post('/products/categories/create/', data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating category:', error);
      throw error;
    }
  },

  updateCategory: async (categoryId, data) => {
    try {
      const response = await apiClient.patch(`/products/categories/${categoryId}/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating category:', error);
      throw error;
    }
  },

  deleteCategory: async (categoryId) => {
    try {
      const response = await apiClient.delete(`/products/categories/${categoryId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error deleting category:', error);
      throw error;
    }
  },

  createGroup: async (data) => {
    try {
      const response = await apiClient.post('/products/groups/create/', data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error creating group:', error);
      throw error;
    }
  },

  updateGroup: async (groupId, data) => {
    try {
      const response = await apiClient.patch(`/products/groups/${groupId}/`, data);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error updating group:', error);
      throw error;
    }
  },

  deleteGroup: async (groupId) => {
    try {
      const response = await apiClient.delete(`/products/groups/${groupId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error deleting group:', error);
      throw error;
    }
  },

  fillMissingBatchesBulk: async (productIds = null) => {
    // productIds=null → auto-detect (products with NULL picks or overdrawn batches).
    // productIds=[...] → selection mode.
    // Now async: returns { job_id, total_targets, mode, status } immediately.
    // Caller polls fillMissingBatchesBulkStatus(job_id) to track progress.
    const body = productIds && productIds.length ? { product_ids: productIds } : {};
    const response = await apiClient.post('/products/fill-missing-batches-bulk/', body);
    return response.data;
  },

  fillMissingBatchesBulkStatus: async (jobId) => {
    const response = await apiClient.get(
      `/products/fill-missing-batches-bulk/status/${jobId}/`
    );
    return response.data;
  },

  bulkGenerateSkus: async (productIds) => {
    try {
      const response = await apiClient.post('/products/bulk-generate-skus/', {
        product_ids: productIds
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk generating SKUs:', error);
      throw error;
    }
  },

  bulkDeleteProducts: async (productIds) => {
    try {
      const response = await apiClient.post('/products/bulk-delete/', {
        product_ids: productIds
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error bulk deleting products:', error);
      throw error;
    }
  },

  copyProduct: async (productId) => {
    try {
      const response = await apiClient.post('/products/copy/', {
        product_id: productId
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error copying product:', error);
      throw error;
    }
  },

  getConsolidationPreview: async (productIds, targetId) => {
    try {
      const response = await apiClient.post('/products/consolidation-preview/', {
        product_ids: productIds,
        target_id: targetId
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error getting consolidation preview:', error);
      throw error;
    }
  },

  consolidateProducts: async (productIds, targetId) => {
    try {
      const response = await apiClient.post('/products/consolidate/', {
        product_ids: productIds,
        target_id: targetId
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Error consolidating products:', error);
      throw error;
    }
  }
};